import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PipCard from "./PipCard";
import {
  useStreamPipStore,
  resetStreamPipStore,
} from "../../store/streamPipStore";
import { useAuthStore } from "../../store/authStore";
import {
  useStreamStatusStore,
  findSlot,
  liveSiblings,
} from "../../store/streamStatusStore";
import type { StreamSlot } from "../../types/stream";
import { slotEntries, slotLabel } from "../../lib/streamSlot";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePipDrag } from "../../hooks/usePipDrag";
import type { WhepState } from "../../lib/whepSession";
import logger from "../../lib/logger";
import { isDocumentPipSupported, openPipWindow } from "../../lib/documentPip";
import { CARD_WIDTH, MOBILE_DOCK_TOP, PIP_WINDOW_SIZE, cornerStyle } from "./dockLayout";
import { Z_DOCK } from "../../lib/zLayers";

export interface PipHostProps {
  /**
   * Inside StreamDock: the card renders in normal flow (no `position: fixed`, no
   * drag transform, no portal); the dock frame owns position, corner and drag,
   * and hands its drag start in as `onDragHandlePointerDown`. Without it the
   * card floats on its own, as before the dock existed.
   */
  docked?: boolean;
  /** Docked: the frame's live slot the card portals into; null while the frame is not shown. */
  container?: HTMLElement | null;
  onDragHandlePointerDown?: (event: React.PointerEvent) => void;
}

export default function PipHost({ docked = false, container = null, onDragHandlePointerDown }: PipHostProps = {}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const mode = useStreamPipStore((s) => s.mode);
  const focusedSlotId = useStreamPipStore((s) => s.focusedSlotId);
  const sessions = useStreamPipStore((s) => s.sessions);
  const identities = useStreamPipStore((s) => s.identities);
  // A stored reference: stable between renders, so safe as a selector. The
  // derived values below are built with useMemo, never inside a selector.
  const infoByGc = useStreamStatusStore((s) => s.infoByGc);
  const corner = useStreamPipStore((s) => s.corner);
  const size = useStreamPipStore((s) => s.size);
  const muted = useStreamPipStore((s) => s.muted);
  const setMuted = useStreamPipStore((s) => s.setMuted);
  const switchTo = useStreamPipStore((s) => s.switchTo);
  const close = useStreamPipStore((s) => s.close);
  const setMode = useStreamPipStore((s) => s.setMode);
  const setCorner = useStreamPipStore((s) => s.setCorner);
  const setSize = useStreamPipStore((s) => s.setSize);
  const setPlayback = useStreamStatusStore((s) => s.setPlayback);

  const session = focusedSlotId == null ? null : (sessions[focusedSlotId] ?? null);
  const identity =
    focusedSlotId == null ? null : (identities[focusedSlotId] ?? null);
  const located = useMemo(
    () =>
      focusedSlotId == null ? undefined : findSlot(infoByGc, focusedSlotId),
    [infoByGc, focusedSlotId],
  );
  // Live data from the status store. When it has nothing for this slot — after
  // a reset, say — fall back to the identity record, so a running session is
  // never left without a card. It then shows no name, only its content type.
  const slot: StreamSlot | null =
    located?.slot ?? (identity ? { ...identity, available: false } : null);

  const [state, setState] = useState<WhepState>("idle");
  const [error, setError] = useState<string | undefined>();
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const wasAuthenticated = useRef(isAuthenticated);

  // "Losing authentication closes everything". Keyed on the auth transition
  // rather than on this component's unmount, so a second route tree (the
  // `backgroundLocation` modal one) closing cannot take the session with it.
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) resetStreamPipStore();
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!session) {
      setState("idle");
      setError(undefined);
      return;
    }
    const sync = () => {
      setState(session.getState());
      setError(session.getError());
    };
    sync();
    return session.subscribe(sync);
  }, [session]);

  useEffect(() => {
    if (!pipWindow) return;
    const onClosed = () => {
      setPipWindow(null);
      // Keep watching in the in-app card rather than dropping the stream.
      if (useStreamPipStore.getState().mode === "popped") setMode("floating");
    };
    pipWindow.addEventListener("pagehide", onClosed);
    return () => pipWindow.removeEventListener("pagehide", onClosed);
  }, [pipWindow, setMode]);

  // Every path that leaves "popped" without going through the ✕ — the stream
  // ending, the availability poll freeing the slot, the user walking back onto
  // the panel — would otherwise unmount the portal and leave a blank
  // always-on-top OS window behind forever.
  useEffect(() => {
    if (!pipWindow || mode === "popped") return;
    pipWindow.close();
    setPipWindow(null);
  }, [pipWindow, mode]);

  const live = useMemo(
    () => (focusedSlotId == null ? [] : liveSiblings(infoByGc, focusedSlotId)),
    [infoByGc, focusedSlotId],
  );

  const drag = usePipDrag({
    enabled: !docked && !isMobile && pipWindow == null,
    onSnap: setCorner,
  });

  if (mode === "panel" || !session || !slot) return null;

  function step(delta: number) {
    if (!slot || live.length < 2) return;
    const index = live.findIndex((entry) => entry.slot.id === slot.id);
    const next = live[(index + delta + live.length) % live.length];
    switchTo(next.slot.id);
  }

  async function popOut() {
    if (isDocumentPipSupported()) {
      const opened = await openPipWindow(PIP_WINDOW_SIZE);
      if (opened) {
        // Mode first: the reconciling effect above closes a window it finds
        // while the mode is anything but "popped".
        setMode("popped");
        setPipWindow(opened);
        return;
      }
    }
    const video = wrapperRef.current?.querySelector("video");
    video
      ?.requestPictureInPicture()
      .catch((err) => logger.warn("[PiP] Native picture-in-picture failed", err));
  }

  function closeEverything() {
    pipWindow?.close();
    setPipWindow(null);
    close();
  }

  /**
   * Android's "tap the PiP window to restore": go back to the panel the stream
   * came from. The session is deliberately left alive — the tile's `acquire()`
   * re-adopts it and flips the mode to "panel" with no reconnect. If that panel
   * never mounts the slot, the card simply keeps playing.
   */
  function expand() {
    const gcId = slot?.gcId;
    navigate(
      gcId == null ? "/street-chat" : `/street-chat?conv=${gcId}&panel=CHANNELS`,
    );
    // Back in the app window, so the separate OS window has no reason to live.
    pipWindow?.close();
    setPipWindow(null);
    if (useStreamPipStore.getState().mode === "popped") setMode("floating");
  }

  const popped = pipWindow != null;
  // Docked and in the app window: the dock frame's live slot (none yet → nothing to show).
  const target = pipWindow?.document.body ?? (docked ? container : document.body);
  // On a browser without Document PiP the fallback is
  // `<video>.requestPictureInPicture()`, and an AUDIO card has no <video>.
  const canPopOut =
    isDocumentPipSupported() || slot.streamContentType !== "AUDIO";

  const card = (
    <div
      ref={wrapperRef}
      data-testid="pip-host"
      data-pip-host
      style={
        popped || docked
          ? { width: "100%" }
          : isMobile
            ? {
                position: "fixed",
                left: 8,
                right: 8,
                top: MOBILE_DOCK_TOP,
                zIndex: Z_DOCK,
              }
            : {
                position: "fixed",
                width: CARD_WIDTH[size],
                maxWidth: "calc(100vw - 24px)",
                zIndex: Z_DOCK,
                ...cornerStyle(corner),
                transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)`,
                transition: drag.dragging ? "none" : "transform 120ms ease-out",
              }
      }
    >
      <PipCard
        slot={slot}
        session={session}
        state={state}
        error={error}
        label={
          located
            ? // By content type, not field: the audio1Id/audio2Id slots now carry VIDEO.
              slotLabel(located.slot, slotEntries(infoByGc[located.gcId]).map((e) => e.slot))
            : slot.streamContentType
        }
        compact={isMobile && !popped}
        muted={muted}
        canSwitch={live.length > 1}
        onToggleMute={() => setMuted(!muted)}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
        onPopOut={canPopOut ? popOut : undefined}
        onExpand={expand}
        onClose={closeEverything}
        onDragHandlePointerDown={
          isMobile || pipWindow != null
            ? undefined
            : docked
              ? onDragHandlePointerDown
              : drag.onPointerDown
        }
        onToggleSize={() => setSize(size === "md" ? "sm" : "md")}
        onPlaybackChange={(playback) => setPlayback(slot.id, playback)}
      />
    </div>
  );

  return target ? createPortal(card, target) : null;
}
